import React, { useEffect, useState } from 'react';

import { Formik, Field } from 'formik';
import * as Yup from 'yup';

import { EyeIcon } from 'CommonComponents/icons';

const IntegrationSaveSchema = Yup.object().shape({
    client_id: Yup.string().required('This value is required'),
    client_secret: Yup.string().required('This value is required'),
    tenant: Yup.string().required('This value is required'),
    environment: Yup.string()
        .max(30)
        .matches(/^[a-zA-Z][a-zA-Z0-9_-]*$/, { message: $t('Invalid environment name') })
        .required('This value is required'),
});

export default function IntegrationsSetupListItemSaveBcForm(props) {
    const [showSecret, setShowSecret] = useState(false);
    const [step, setStep] = useState(1);
    const [companies, setCompanies] = useState([]);
    const [company, setCompany] = useState(props.integration ? props.integration.config.company : {});
    const [submitDisabled, setSubmitDisabled] = useState(false);

    useEffect(() => {
        const result = step === 2 ? Object.keys(company).length === 0 : false;

        setSubmitDisabled(result);
    }, [step, company]);

    useEffect(() => {
        if (props.integration) {
            setCompanies([props.integration.config.company]);
        }
    }, []);

    const testConnection = (data, actions) => {
        props.setError(null);
        props.setRequest(true);
        data.code = props.provider.code;
        data.provider_id = props.provider.id;
        data.config = {
            tenant: data.tenant,
            environment: data.environment,
        };

        axios
            .post(props.testConnectionUrl, data)
            .then((response) => {
                setCompanies(response.data.connection);
                setStep(2);
            })
            .catch((e) => {
                const err = e.response?.data?.message || e.response?.data?.error;
                if (err) {
                    props.setError(err);
                }
                props.setIntegrationCreated(false);
                props.setRequestFailed(true);
                if (actions) {
                    actions.setErrors(e.response.data.errors);
                }
            })
            .finally(() => {
                props.setRequest(false);
                if (actions) {
                    actions.setSubmitting(false);
                }
            });
    };

    const createIntegration = (data, actions) => {
        props.setError(null);
        props.setRequest(true);
        data.code = props.provider.code;
        data.provider_id = props.provider.id;
        data.config = {
            company,
            environment: data.environment,
            tenant: data.tenant,
        };

        axios
            .post(props.storeUrl, data)
            .then((response) => {
                props.setIntegrationCreated(true);
            })
            .catch((e) => {
                const err = e.response?.data?.message || e.response?.data?.error;
                if (err) {
                    props.setError(err);
                }
                props.setIntegrationCreated(false);
                props.setRequestFailed(true);
                if (actions) {
                    actions.setErrors(e.response.data.errors);
                }
            })
            .finally(() => {
                props.setRequest(false);
                if (actions) {
                    actions.setSubmitting(false);
                }
            });
    };

    const updateIntegration = (data, actions) => {
        props.setError(null);
        props.setRequest(true);
        const url = props.updateUrl.replace('INTEGRATION_ID', props.provider.integration.id);
        data.config = {
            company,
            environment: data.environment,
            tenant: data.tenant,
        };

        axios
            .put(url, data)
            .then((response) => {
                props.setIntegrationCreated(true);
            })
            .catch((e) => {
                const err = e.response?.data?.message || e.response?.data?.error;
                if (err) {
                    props.setError(err);
                }
                props.setIntegrationCreated(false);
                props.setRequestFailed(true);
                if (actions) {
                    actions.setErrors(e?.response?.data?.errors);
                }
            })
            .finally(() => {
                props.setRequest(false);
                if (actions) {
                    actions.setSubmitting(false);
                }
            });
    };

    const changeCompany = (e) => {
        const id = e.target.value;

        const item = companies.find((c) => {
            return c.id === id;
        });
        const data = {
            id,
            name: item ? item.name : '',
        };

        setCompany(data);
    };

    return (
        <Formik
            initialValues={{
                client_id: props.integration ? props.integration.raw_client_id : '',
                client_secret: props.integration ? props.integration.raw_client_secret : '',
                tenant: props.integration ? props.integration.config.tenant : '',
                environment: props.integration ? props.integration.config.environment : '',
            }}
            validationSchema={IntegrationSaveSchema}
            onSubmit={(values, actions) => {
                if (props.action === 'create') {
                    if (step === 1) {
                        testConnection(values, actions);
                    } else {
                        createIntegration(values, actions);
                    }
                } else {
                    updateIntegration(values, actions);
                }
            }}
        >
            {({ values, errors, touched, isSubmitting, handleSubmit, setFieldValue }) => (
                <>
                    <div className="tw-flex tw-flex-row tw-my-3 tw-items-center tw-text-left">
                        <label htmlFor="client_id" className="tw-basis-3/12 tw-font-bold">
                            {$t('App Id')} <span className="tw-text-red-500">*</span>
                        </label>
                        <div className="tw-basis-9/12">
                            <Field
                                type="text"
                                className={errors && errors.client_id && touched.client_id ? 'input text-input invalid' : 'input text-input'}
                                name="client_id"
                                placeholder={$t('App Id')}
                            />
                            {errors && errors.client_id && touched.client_id && <div className="tw-text-red-500">{errors.client_id}</div>}
                        </div>
                    </div>

                    <div className="tw-flex tw-flex-row tw-my-3 tw-items-center tw-text-left">
                        <label htmlFor="client_secret" className="tw-basis-3/12 tw-font-bold">
                            {$t('App Secret')} <span className="tw-text-red-500">*</span>
                        </label>
                        <div className="tw-basis-9/12 tw-relative">
                            <Field
                                type={showSecret ? 'text' : 'password'}
                                className={errors && errors.client_secret && touched.client_secret ? 'input text-input invalid' : 'input text-input'}
                                name="client_secret"
                                placeholder={$t('App Secret')}
                            />
                            <span className="tw-absolute tw-cursor-pointer" style={{ top: '35%', right: '1%' }} onClick={(e) => setShowSecret(!showSecret)}>
                                <EyeIcon className="tw-w-3 tw-h-3" />
                            </span>
                            {errors && errors.client_secret && touched.client_secret && <div className="tw-text-red-500">{errors.client_secret}</div>}
                        </div>
                    </div>

                    <div className="tw-flex tw-flex-row tw-my-3 tw-items-center tw-text-left">
                        <label htmlFor="tenant" className="tw-basis-3/12 tw-font-bold">
                            {$t('Tenant Id')} <span className="tw-text-red-500">*</span>
                        </label>
                        <div className="tw-basis-9/12">
                            <Field
                                type="text"
                                className={errors && errors.tenant && touched.tenant ? 'input text-input invalid' : 'input text-input'}
                                name="tenant"
                                placeholder={$t('Tenant Id')}
                                disabled={props.action === 'update'}
                            />
                            {errors && errors.tenant && touched.tenant && <div className="tw-text-red-500">{errors.tenant}</div>}
                        </div>
                    </div>

                    <div className="tw-flex tw-flex-row tw-my-3 tw-items-center tw-text-left">
                        <label htmlFor="environment" className="tw-basis-3/12 tw-font-bold">
                            {$t('Environment')} <span className="tw-text-red-500">*</span>
                        </label>
                        <div className="tw-basis-9/12">
                            <Field
                                type="text"
                                className={errors && errors.environment && touched.environment ? 'input text-input invalid' : 'input text-input'}
                                name="environment"
                                placeholder={$t('Example: production')}
                                disabled={props.action !== 'create'}
                            />
                            {errors && errors.environment && touched.environment && <div className="tw-text-red-500">{errors.environment}</div>}
                        </div>
                    </div>

                    {(step === 2 || props.action === 'update') && (
                        <>
                            <div className="tw-flex tw-flex-row tw-my-3 tw-items-center tw-text-left">
                                <label htmlFor="company" className="tw-basis-3/12 tw-font-bold">
                                    {$t('Company')} <span className="tw-text-red-500">*</span>
                                </label>
                                <div className="tw-basis-9/12">
                                    <select
                                        className="select select-input"
                                        value={company ? company.id : ''}
                                        onChange={changeCompany}
                                        disabled={props.action !== 'create'}
                                    >
                                        {Object.keys(company).length === 0 && <option value={''}>{$t('Choose company')}</option>}
                                        {companies.map((item) => {
                                            return (
                                                <option key={item.id} value={item.id}>
                                                    {item.name}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </div>
                            </div>
                        </>
                    )}

                    <div className="modal-footer">
                        {props.error && <div className="tw-text-red-500 tw-py-2">{props.error}</div>}
                        <button type="button" className="button danger" onClick={props.internalCloseHandler} disabled={props.request}>
                            {$t('Cancel')}
                        </button>
                        <button type="button" className="button primary" onClick={handleSubmit} disabled={props.request || submitDisabled}>
                            {props.request && <i className="fa fa-spinner fa-spin " aria-hidden="true"></i>}
                            {step === 1 && props.action === 'create' ? $t('Test Connection') : $t('Save')}
                        </button>
                    </div>
                </>
            )}
        </Formik>
    );
}
